import Phaser from 'phaser';
import {
  ensureHeroTextures,
  HERO_COMBAT_TEXTURE_KEY,
  HERO_PAUSE_TEXTURE_KEY,
} from './characterVisuals';
import {
  COLORS,
  PROTOTYPE_DURATION_SECONDS,
  VIEW_HEIGHT,
  VIEW_WIDTH,
  WORLD_HEIGHT,
  WORLD_WIDTH,
} from './constants';
import {
  canRangedEnemyShoot,
  getRangedMovementMode,
  getRangedSpawnChance,
  RANGED_ENEMY,
  type EnemyKind,
} from './enemyLogic';
import {
  applyUpgrade,
  chooseUpgradeOffers,
  DEFAULT_STATS,
  describeUpgradeChanges,
  getTakenUpgradeBadges,
  getTierDefinition,
  type PlayerStats,
  type UpgradeIcon,
  type UpgradeOffer,
  type UpgradeRanks,
} from './upgrades';
import {
  getUpgradeCardCenterX,
  getUpgradeChoiceIndexAt,
  UPGRADE_CARD_CENTER_Y,
  UPGRADE_CARD_HEIGHT,
  UPGRADE_CARD_WIDTH,
} from './upgradeLayout';
import {
  applyHealing,
  CAMPFIRE_HEAL_PER_SECOND,
  getRecoveryPerSecond,
  getVampirismHealing,
} from './recovery';
import {
  CAMPFIRE_SPOTS,
  isPointBlocked,
  MAP_OBSTACLES,
  PLAYER_SPAWN,
  type MapObstacle,
} from './worldLayout';
import { loadNickname, recordRunResult } from './playerProfile';
import {
  getPauseOverlayActionAt,
  getResultOverlayActionAt,
  type ResultOverlayAction,
} from './overlayTouchLayout';

type MovementKeys = {
  up: Phaser.Input.Keyboard.Key;
  down: Phaser.Input.Keyboard.Key;
  left: Phaser.Input.Keyboard.Key;
  right: Phaser.Input.Keyboard.Key;
};

const SOUND_SETTING_KEY = 'kuogam.sound-enabled';

function splitUpgradeChange(change: string): { label: string; value: string } {
  const match = change.match(/^(.+?)\s+(-?[\d].*)$/);
  if (!match) return { label: 'ИЗМЕНЕНИЕ', value: change };
  return { label: match[1]!.toUpperCase(), value: match[2]! };
}

export class GameScene extends Phaser.Scene {
  private player!: Phaser.Physics.Arcade.Sprite;
  private enemies!: Phaser.Physics.Arcade.Group;
  private enemyProjectiles!: Phaser.Physics.Arcade.Group;
  private shards!: Phaser.Physics.Arcade.Group;
  private obstacles!: Phaser.Physics.Arcade.StaticGroup;
  private cursors?: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd?: MovementKeys;

  private stats: PlayerStats = { ...DEFAULT_STATS };
  private upgradeRanks: UpgradeRanks = {};
  private level = 1;
  private xp = 0;
  private xpToNext = 5;
  private kills = 0;
  private elapsedMs = 0;
  private spawnAccumulatorMs = 0;
  private lastAttackAt = 0;
  private damageInvulnerableUntil = 0;
  private choosingUpgrade = false;
  private ended = false;
  private externallyPaused = false;
  private manuallyPaused = false;
  private soundEnabled = true;
  private resultTransitioning = false;

  private joystickBase!: Phaser.GameObjects.Arc;
  private joystickKnob!: Phaser.GameObjects.Arc;
  private joystickPointerId: number | null = null;
  private joystickVector = new Phaser.Math.Vector2();

  private xpBar!: Phaser.GameObjects.Graphics;
  private hpBar!: Phaser.GameObjects.Graphics;
  private levelText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private killText!: Phaser.GameObjects.Text;
  private fpsText!: Phaser.GameObjects.Text;
  private hpText!: Phaser.GameObjects.Text;
  private campfireStatusText!: Phaser.GameObjects.Text;
  private takenUpgradesHud!: Phaser.GameObjects.Container;
  private upgradeOverlay?: Phaser.GameObjects.Container;
  private upgradeToast?: Phaser.GameObjects.Container;
  private pauseOverlay?: Phaser.GameObjects.Container;
  private pauseSoundButtonText?: Phaser.GameObjects.Text;
  private swordWhooshBuffer?: AudioBuffer;
  private activeUpgradeChoices: UpgradeOffer[] = [];

  constructor() {
    super('game');
  }

  create(): void {
    this.resetState();
    this.loadSoundSetting();
    this.createProceduralTextures();

    this.physics.world.setBounds(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    this.physics.world.resume();
    this.obstacles = this.physics.add.staticGroup();
    this.createEnvironment();

    this.enemies = this.physics.add.group({ maxSize: 180 });
    this.enemyProjectiles = this.physics.add.group({ maxSize: 120 });
    this.shards = this.physics.add.group({ maxSize: 260 });

    this.player = this.physics.add.sprite(PLAYER_SPAWN.x, PLAYER_SPAWN.y, HERO_COMBAT_TEXTURE_KEY);
    this.player.setScale(0.72);
    this.player.setCollideWorldBounds(true);
    this.player.setCircle(23, 25, 66);
    this.player.setDepth(this.player.y);

    this.physics.add.collider(this.player, this.obstacles);
    this.physics.add.collider(this.enemies, this.obstacles);
    this.physics.add.collider(
      this.enemyProjectiles,
      this.obstacles,
      (projectileObject) => {
        const projectile = projectileObject as Phaser.Physics.Arcade.Sprite;
        if (projectile.active) projectile.disableBody(true, true);
      },
    );

    this.cameras.main.setBounds(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    this.cameras.main.setRoundPixels(true);
    this.cameras.main.startFollow(this.player, true, 0.11, 0.11);

    this.createControls();
    this.createHud();
    this.bindLifecycle();

    const previewScreen = new URLSearchParams(window.location.search).get('screen');
    const spawnInitialEnemies = () => {
      for (let index = 0; index < 5; index += 1) {
        this.spawnEnemy(index === 0 ? 'ranged' : undefined);
      }
    };
    if (previewScreen === 'gameplay') {
      spawnInitialEnemies();
      const previewOffsets = [
        { x: 0, y: -175 },
        { x: -145, y: -45 },
        { x: 145, y: -35 },
        { x: -112, y: 145 },
        { x: 120, y: 155 },
      ];
      this.enemies.getChildren().forEach((child, index) => {
        const enemy = child as Phaser.Physics.Arcade.Sprite;
        const offset = previewOffsets[index];
        if (!enemy.active || !offset) return;
        enemy.setPosition(this.player.x + offset.x, this.player.y + offset.y);
        enemy.setDepth(enemy.y);
        this.updateEnemyHpLabel(enemy);
      });
    } else this.time.delayedCall(700, spawnInitialEnemies);

    if (previewScreen === 'levelup') this.showUpgradeChoices();
    if (previewScreen === 'pause') this.openPause();
    if (previewScreen === 'result' && !this.registry.get('result-preview-shown')) {
      this.registry.set('result-preview-shown', true);
      this.endRun(false);
    }
  }

  update(time: number, delta: number): void {
    if (this.ended || this.choosingUpgrade || this.externallyPaused || this.manuallyPaused) return;

    const safeDelta = Math.min(delta, 50);
    this.elapsedMs += safeDelta;
    this.spawnAccumulatorMs += safeDelta;

    this.updateMovement();
    this.updateHealthRecovery(safeDelta);
    this.updateEnemies(time);
    this.updateEnemyProjectiles(time);
    this.updateShards();
    this.updateSpawning();
    this.tryAttack(time);
    this.updateHud();

    if (this.elapsedMs >= PROTOTYPE_DURATION_SECONDS * 1000) this.endRun(true);
  }

  private resetState(): void {
    this.stats = { ...DEFAULT_STATS };
    this.upgradeRanks = {};
    this.level = 1;
    this.xp = 0;
    this.xpToNext = 5;
    this.kills = 0;
    this.elapsedMs = 0;
    this.spawnAccumulatorMs = 0;
    this.lastAttackAt = 0;
    this.damageInvulnerableUntil = 0;
    this.choosingUpgrade = false;
    this.ended = false;
    this.externallyPaused = false;
    this.manuallyPaused = false;
    this.resultTransitioning = false;
    this.joystickPointerId = null;
    this.joystickVector.set(0, 0);
    this.upgradeOverlay = undefined;
    this.upgradeToast = undefined;
    this.pauseOverlay = undefined;
    this.pauseSoundButtonText = undefined;
    this.activeUpgradeChoices = [];
  }

  private createProceduralTextures(): void {
    ensureHeroTextures(this);

    if (!this.textures.exists('plague-peasant')) {
      const enemy = this.add.graphics().setVisible(false);
      enemy.fillStyle(0x000000, 0.3).fillEllipse(21, 47, 34, 11);
      enemy.fillStyle(0x4b4b3b, 1).fillTriangle(8, 20, 34, 17, 38, 47);
      enemy.fillStyle(0x232821, 1).fillCircle(21, 14, 10);
      enemy.fillStyle(0x83906a, 0.8).fillCircle(18, 13, 2).fillCircle(25, 13, 2);
      enemy.lineStyle(5, 0x4a4136, 1).lineBetween(8, 27, 1, 40).lineBetween(34, 26, 41, 38);
      enemy.generateTexture('plague-peasant', 44, 54);
      enemy.destroy();
    }

    if (!this.textures.exists('plague-cultist')) {
      const cultist = this.add.graphics().setVisible(false);
      cultist.fillStyle(0x000000, 0.34).fillEllipse(24, 54, 38, 11);
      cultist.fillStyle(0x2b222d, 1).fillTriangle(6, 52, 23, 13, 42, 52);
      cultist.fillStyle(0x49384c, 1).fillTriangle(11, 47, 23, 18, 35, 47);
      cultist.fillStyle(0x151718, 1).fillCircle(23, 15, 12);
      cultist.fillStyle(0xa98958, 0.9).fillCircle(19, 15, 2).fillCircle(27, 15, 2);
      cultist.lineStyle(5, 0x44392d, 1).lineBetween(39, 6, 43, 52);
      cultist.fillStyle(0xc27a3c, 0.34).fillCircle(38, 5, 9);
      cultist.fillStyle(0xd49a55, 0.92).fillCircle(38, 5, 4);
      cultist.lineStyle(3, 0x665568, 0.8).lineBetween(12, 37, 34, 37);
      cultist.generateTexture('plague-cultist', 48, 60);
      cultist.destroy();
    }

    if (!this.textures.exists('cultist-bolt')) {
      const bolt = this.add.graphics().setVisible(false);
      bolt.fillStyle(0xd58543, 0.18).fillCircle(9, 9, 9);
      bolt.fillStyle(0xe3a15b, 0.92).fillCircle(9, 9, 5);
      bolt.fillStyle(0xffddb0, 0.96).fillCircle(9, 9, 2);
      bolt.lineStyle(2, 0x6a3540, 0.72).strokeCircle(9, 9, 6);
      bolt.generateTexture('cultist-bolt', 18, 18);
      bolt.destroy();
    }

    if (!this.textures.exists('xp-shard')) {
      const shard = this.add.graphics().setVisible(false);
      shard.fillStyle(COLORS.xp, 0.2).fillCircle(9, 9, 9);
      shard.fillStyle(COLORS.xp, 1).fillTriangle(9, 1, 15, 8, 9, 17);
      shard.fillStyle(0xb9eee2, 1).fillTriangle(9, 1, 9, 17, 4, 8);
      shard.generateTexture('xp-shard', 18, 18);
      shard.destroy();
    }
  }

  private createEnvironment(): void {
    this.drawVillageGround();

    for (const obstacle of MAP_OBSTACLES) {
      if (obstacle.kind === 'building') this.drawBuilding(obstacle);
      else if (obstacle.kind === 'fence') this.drawFence(obstacle);
      else if (obstacle.kind === 'tree') this.drawDeadTree(obstacle);
      else this.drawVillageProp(obstacle);
      this.addObstacleBody(obstacle);
    }

    this.drawCemetery(650, 1280);
    this.drawCemetery(1120, 1660);
    this.drawCemetery(450, 2320);
    CAMPFIRE_SPOTS.forEach((campfire, index) => this.drawCampfire(
      campfire.x,
      campfire.y,
      campfire.radius,
      index * 115,
    ));
  }

  private drawVillageGround(): void {
    const ground = this.add.graphics().setDepth(-1000);
    ground.fillStyle(COLORS.ground, 1).fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    ground.fillStyle(0x101410, 0.9).fillEllipse(WORLD_WIDTH / 2, WORLD_HEIGHT / 2, 1460, 2460);

    // Дальние поля: редкие борозды создают масштаб, но не выглядят игровой сеткой.
    ground.lineStyle(2, 0x31362d, 0.2);
    for (let y = 120; y < WORLD_HEIGHT; y += 92) {
      ground.lineBetween(70, y, 410, y + 48);
      ground.lineBetween(1390, y + 34, 1730, y - 12);
    }

    const drawRoad = (points: Array<{ x: number; y: number }>, width: number): void => {
      ground.lineStyle(width + 34, 0x0b0e0c, 0.7);
      ground.beginPath().moveTo(points[0]!.x, points[0]!.y);
      points.slice(1).forEach((point) => ground.lineTo(point.x, point.y));
      ground.strokePath();
      ground.lineStyle(width, 0x29281f, 0.92);
      ground.beginPath().moveTo(points[0]!.x, points[0]!.y);
      points.slice(1).forEach((point) => ground.lineTo(point.x, point.y));
      ground.strokePath();
      ground.lineStyle(3, 0x58513d, 0.2);
      ground.beginPath().moveTo(points[0]!.x - 42, points[0]!.y);
      points.slice(1).forEach((point) => ground.lineTo(point.x - 42, point.y));
      ground.strokePath();
      ground.beginPath().moveTo(points[0]!.x + 42, points[0]!.y);
      points.slice(1).forEach((point) => ground.lineTo(point.x + 42, point.y));
      ground.strokePath();
    };

    drawRoad([
      { x: 910, y: -80 },
      { x: 960, y: 560 },
      { x: 875, y: 1080 },
      { x: 900, y: 1450 },
      { x: 1010, y: 1910 },
      { x: 925, y: 2390 },
      { x: 980, y: WORLD_HEIGHT + 80 },
    ], 174);
    drawRoad([
      { x: 260, y: 1430 },
      { x: 620, y: 1475 },
      { x: 900, y: 1450 },
      { x: 1180, y: 1390 },
      { x: 1540, y: 1450 },
    ], 124);
    drawRoad([
      { x: 870, y: 1060 },
      { x: 1120, y: 1140 },
      { x: 1320, y: 1290 },
    ], 86);
    drawRoad([
      { x: 970, y: 1900 },
      { x: 760, y: 2050 },
      { x: 620, y: 2280 },
    ], 92);

    // Каменный двор часовни и вытоптанная деревенская площадь.
    ground.fillStyle(0x35352e, 0.7).fillEllipse(900, 1050, 360, 250);
    ground.lineStyle(4, 0x5d5b50, 0.28).strokeEllipse(900, 1050, 350, 240);
    ground.fillStyle(0x302d23, 0.66).fillEllipse(900, 1460, 480, 330);
    ground.lineStyle(3, 0x5b513d, 0.22).strokeEllipse(900, 1460, 465, 315);

    // Плоские камни и колеи на дорогах.
    for (let index = 0; index < 46; index += 1) {
      const y = 100 + index * 58;
      const x = 915 + Math.sin(index * 1.73) * 58;
      const size = 7 + (index % 4) * 2;
      ground.fillStyle(index % 3 === 0 ? 0x55564d : 0x45483f, 0.38);
      ground.fillEllipse(x, y, size * 1.7, size);
    }
    for (let index = 0; index < 18; index += 1) {
      const x = 300 + index * 68;
      const y = 1450 + Math.sin(index * 0.9) * 36;
      ground.fillStyle(0x555247, 0.3).fillEllipse(x, y, 18, 9);
    }

    // Холодные лужи и болотная трава по краям дорог.
    [
      { x: 760, y: 880, w: 92, h: 31 },
      { x: 1070, y: 1320, w: 78, h: 25 },
      { x: 900, y: 1810, w: 112, h: 34 },
      { x: 1190, y: 2240, w: 85, h: 28 },
      { x: 520, y: 2020, w: 74, h: 24 },
    ].forEach((puddle) => {
      ground.fillStyle(0x273536, 0.5).fillEllipse(puddle.x, puddle.y, puddle.w, puddle.h);
      ground.lineStyle(2, 0x617070, 0.18).strokeEllipse(puddle.x, puddle.y, puddle.w, puddle.h);
    });
    ground.lineStyle(3, 0x46513f, 0.34);
    for (let index = 0; index < 34; index += 1) {
      const x = 170 + ((index * 137) % 1460);
      const y = 190 + ((index * 223) % 2420);
      ground.lineBetween(x, y, x - 5, y - 15);
      ground.lineBetween(x, y, x + 7, y - 12);
    }

    // Затемнение границы держит внимание внутри деревни.
    ground.lineStyle(170, 0x050706, 0.54).strokeRect(30, 30, WORLD_WIDTH - 60, WORLD_HEIGHT - 60);
  }

  private drawBuilding(obstacle: MapObstacle): void {
    const { x, y, width, height, variant } = obstacle;
    const anchorY = variant === 'chapel' ? y + 40 : y + 21;
    const building = this.add.graphics({ x, y: anchorY }).setDepth(y + height / 2);
    building.fillStyle(0x070908, 0.48).fillEllipse(9, 31, width + 34, 34);

    if (variant === 'chapel') {
      building.fillStyle(0x25241f, 1).fillRect(-70, -115, 140, 150);
      building.fillStyle(0x171916, 1).fillTriangle(-85, -115, 0, -185, 85, -115);
      building.fillStyle(0x30302a, 1).fillRect(-22, -174, 44, 65);
      building.fillStyle(0x11130f, 1).fillTriangle(-32, -174, 0, -208, 32, -174);
      building.fillStyle(COLORS.ember, 0.72).fillRect(-10, -68, 20, 40);
      building.lineStyle(5, 0x4b4539, 1).strokeRect(-70, -115, 140, 150);
      building.lineStyle(2, 0x5a554a, 0.56).lineBetween(-50, -82, -10, -104);
      building.lineBetween(24, -91, 57, -70);
      return;
    }

    if (variant === 'burning-house') {
      building.fillStyle(0x302820, 1).fillRect(-62, -72, 124, 102);
      building.fillStyle(0x171612, 1).fillTriangle(-75, -72, 0, -125, 75, -72);
      building.fillStyle(COLORS.ember, 0.82).fillCircle(-24, -38, 14).fillCircle(20, -48, 19);
      building.fillStyle(0xf0b45b, 0.68).fillCircle(9, -28, 10);
      building.lineStyle(5, 0x46392c, 1).strokeRect(-62, -72, 124, 102);
      building.lineStyle(4, 0x17120f, 0.9).lineBetween(-54, -63, 51, 19);
      return;
    }

    if (variant === 'ruined-house') {
      building.fillStyle(0x2b2a24, 1).fillRect(-width / 2, -69, width, 96);
      building.fillStyle(0x171815, 1).fillTriangle(-width / 2 - 9, -69, -22, -116, 18, -69);
      building.fillStyle(0x151714, 1).fillTriangle(8, -69, 42, -101, width / 2 + 8, -69);
      building.fillStyle(0x0c0e0c, 1).fillRect(-18, -30, 36, 57);
      building.lineStyle(5, 0x4d493e, 1).lineBetween(-width / 2, 27, -width / 2, -69);
      building.lineBetween(width / 2, 27, width / 2, -69);
      building.lineStyle(3, 0x171815, 0.9).lineBetween(-55, -58, 46, 13);
      return;
    }

    building.fillStyle(0x332d24, 1).fillRect(-87, -79, 174, 116);
    building.fillStyle(0x181713, 1).fillTriangle(-99, -79, 0, -137, 99, -79);
    building.fillStyle(0x121411, 1).fillRect(-31, -36, 62, 73);
    building.lineStyle(7, 0x4d4030, 1).strokeRect(-87, -79, 174, 116);
    building.lineStyle(3, 0x66523c, 0.72).lineBetween(-84, -50, 84, 11);
    building.lineBetween(-84, 10, 84, -49);
  }

  private drawFence(obstacle: MapObstacle): void {
    const { x, y, width, height } = obstacle;
    const horizontal = width >= height;
    const fence = this.add.graphics({ x, y }).setDepth(y + height / 2 + 2);
    fence.fillStyle(0x060807, 0.42).fillEllipse(5, 9, width + 18, Math.max(18, height + 7));
    fence.lineStyle(7, 0x30271e, 1);

    if (horizontal) {
      fence.lineBetween(-width / 2, -5, width / 2, -5);
      fence.lineBetween(-width / 2, 6, width / 2, 6);
      for (let offset = -width / 2 + 10; offset <= width / 2; offset += 42) {
        fence.lineStyle(8, 0x51402e, 1).lineBetween(offset, -18, offset, 18);
        fence.lineStyle(2, 0x766047, 0.62).lineBetween(offset - 2, -15, offset - 2, 14);
      }
    } else {
      fence.lineBetween(-5, -height / 2, -5, height / 2);
      fence.lineBetween(6, -height / 2, 6, height / 2);
      for (let offset = -height / 2 + 10; offset <= height / 2; offset += 42) {
        fence.lineStyle(8, 0x51402e, 1).lineBetween(-18, offset, 18, offset);
        fence.lineStyle(2, 0x766047, 0.62).lineBetween(-14, offset - 2, 14, offset - 2);
      }
    }
  }

  private drawDeadTree(obstacle: MapObstacle): void {
    const { x, y, width } = obstacle;
    const tree = this.add.graphics({ x, y }).setDepth(y + 24);
    tree.fillStyle(0x060807, 0.48).fillEllipse(8, 12, width + 28, 24);
    tree.lineStyle(15, 0x342a22, 1).lineBetween(0, 9, -2, -67);
    tree.lineStyle(7, 0x4b392b, 1);
    tree.lineBetween(-2, -53, -34, -87);
    tree.lineBetween(-5, -42, 31, -72);
    tree.lineBetween(-33, -87, -42, -105);
    tree.lineBetween(-30, -83, -52, -77);
    tree.lineBetween(30, -72, 41, -94);
    tree.lineBetween(28, -68, 51, -63);
    tree.lineStyle(3, 0x6b5038, 0.66).lineBetween(-5, 4, -8, -61);
    tree.fillStyle(0x252d25, 0.76).fillCircle(-36, -89, 8).fillCircle(38, -77, 7);
  }

  private drawVillageProp(obstacle: MapObstacle): void {
    const { x, y, variant } = obstacle;
    const prop = this.add.graphics({ x, y }).setDepth(y + obstacle.height / 2 + 4);

    if (variant === 'village-well') {
      prop.fillStyle(0x050706, 0.55).fillEllipse(8, 18, 96, 31);
      prop.fillStyle(0x55574f, 1).fillEllipse(0, 0, 70, 42);
      prop.fillStyle(0x30342f, 1).fillEllipse(0, -3, 57, 29);
      prop.fillStyle(0x090b0a, 1).fillEllipse(0, -5, 43, 20);
      prop.lineStyle(5, 0x77796e, 0.82).strokeEllipse(0, 0, 70, 42);
      prop.lineStyle(7, 0x4b3929, 1).lineBetween(-31, -4, -31, -76);
      prop.lineBetween(31, -4, 31, -76);
      prop.lineStyle(8, 0x36291f, 1).lineBetween(-42, -72, 42, -72);
      prop.fillStyle(0x201b16, 1).fillTriangle(-48, -72, 0, -102, 48, -72);
      prop.lineStyle(3, 0x796149, 0.62).lineBetween(0, -70, 0, -22);
      prop.fillStyle(0x55402d, 1).fillRoundedRect(-9, -32, 18, 14, 3);
      return;
    }

    if (variant === 'broken-cart') {
      prop.fillStyle(0x050706, 0.5).fillEllipse(7, 15, 142, 34);
      prop.lineStyle(7, 0x30241c, 1).strokeCircle(-38, 11, 24);
      prop.strokeCircle(39, 11, 24);
      prop.lineStyle(3, 0x72543a, 0.72);
      for (let angle = 0; angle < 360; angle += 45) {
        const radians = Phaser.Math.DegToRad(angle);
        prop.lineBetween(-38, 11, -38 + Math.cos(radians) * 20, 11 + Math.sin(radians) * 20);
        prop.lineBetween(39, 11, 39 + Math.cos(radians) * 20, 11 + Math.sin(radians) * 20);
      }
      prop.fillStyle(0x4a3525, 1).fillPoints([
        { x: -53, y: -25 }, { x: 48, y: -19 }, { x: 58, y: 10 }, { x: -60, y: 6 },
      ], true);
      prop.lineStyle(4, 0x76543a, 0.7).lineBetween(-45, -14, 48, -9);
      prop.lineStyle(8, 0x38271d, 1).lineBetween(48, -9, 91, -31);
      prop.lineStyle(5, 0x69482e, 0.82).lineBetween(71, -20, 99, -6);
      return;
    }

    prop.fillStyle(0x050706, 0.52).fillEllipse(9, 18, 98, 27);
    prop.lineStyle(13, 0x35281e, 1).lineBetween(-27, 15, -27, -106);
    prop.lineStyle(12, 0x3f3023, 1).lineBetween(-31, -103, 41, -103);
    prop.lineStyle(7, 0x59412d, 1).lineBetween(-45, 17, -9, 17);
    prop.lineStyle(4, 0x8d7652, 0.78).lineBetween(29, -101, 29, -68);
    prop.lineStyle(3, 0xa88d62, 0.72).strokeCircle(29, -58, 10);
    prop.lineStyle(6, 0x2b2019, 1).lineBetween(-27, -77, 7, -103);
  }

  private addObstacleBody(obstacle: MapObstacle): void {
    const hitbox = this.add
      .rectangle(obstacle.x, obstacle.y, obstacle.width, obstacle.height, 0xffffff, 0)
      .setVisible(false);
    this.obstacles.add(hitbox);
    const body = hitbox.body as Phaser.Physics.Arcade.StaticBody;
    body.setSize(obstacle.width, obstacle.height);
    body.updateFromGameObject();
  }

  private drawCampfire(x: number, y: number, radius: number, delay: number): void {
    const healingZone = this.add
      .circle(x, y, radius, 0xd69a53, 0.045)
      .setStrokeStyle(2, 0xd9a65c, 0.18)
      .setDepth(y - 110);
    const glow = this.add.circle(x, y - 2, 47, 0xe2843e, 0.11).setDepth(y - 4);
    const fire = this.add.graphics({ x, y }).setDepth(y + 5);
    fire.fillStyle(0x070807, 0.52).fillEllipse(5, 17, 82, 25);
    for (let angle = 0; angle < 360; angle += 45) {
      const radians = Phaser.Math.DegToRad(angle);
      fire.fillStyle(angle % 90 === 0 ? 0x777064 : 0x56554e, 1);
      fire.fillEllipse(Math.cos(radians) * 25, Math.sin(radians) * 11 + 7, 18, 12);
    }
    fire.lineStyle(8, 0x4a2f20, 1).lineBetween(-20, 10, 20, -7);
    fire.lineStyle(7, 0x6b4127, 1).lineBetween(-20, -7, 20, 11);
    fire.fillStyle(0xc84b2e, 0.95).fillTriangle(-17, 7, -2, -40, 8, 8);
    fire.fillStyle(COLORS.ember, 1).fillTriangle(-7, 7, 10, -49, 17, 7);
    fire.fillStyle(0xf2c064, 1).fillTriangle(-3, 6, 5, -27, 11, 6);
    const label = this.add
      .text(x, y + 41, '+ HP', {
        fontSize: '10px',
        fontStyle: 'bold',
        color: '#d5ad72',
        stroke: '#090706',
        strokeThickness: 3,
        letterSpacing: 1,
      })
      .setOrigin(0.5, 0)
      .setDepth(y + 6);

    this.tweens.add({
      targets: [glow, fire],
      scaleX: 1.07,
      scaleY: 0.94,
      alpha: { from: 0.88, to: 1 },
      duration: 480,
      delay,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: healingZone,
      alpha: { from: 0.52, to: 0.9 },
      duration: 1200,
      delay,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    label.setAlpha(0.86);
  }

  private drawCemetery(x: number, y: number): void {
    const cemetery = this.add.graphics({ x, y }).setDepth(y - 30);
    cemetery.fillStyle(0x0c100d, 0.62).fillRoundedRect(-82, -112, 164, 232, 18);
    cemetery.lineStyle(2, 0x3e463d, 0.36).strokeRoundedRect(-82, -112, 164, 232, 18);
    for (let row = 0; row < 4; row += 1) {
      for (let column = 0; column < 3; column += 1) {
        const graveX = column * 42 - 42 + (row % 2) * 7;
        const graveY = row * 54 - 75;
        cemetery.fillStyle(0x090b09, 0.46).fillEllipse(graveX + 6, graveY + 17, 31, 11);
        cemetery.fillStyle(0x4e5149, 1).fillRoundedRect(graveX - 9, graveY - 21, 18, 34, 7);
        cemetery.lineStyle(2, 0x74786d, 0.5).lineBetween(graveX, graveY - 16, graveX, graveY + 7);
        if ((row + column) % 2 === 0) {
          cemetery.lineBetween(graveX - 7, graveY - 8, graveX + 7, graveY - 8);
        }
        cemetery.fillStyle(0x273126, 0.7).fillCircle(graveX - 15, graveY + 11, 4);
      }
    }
  }

  private createControls(): void {
    this.joystickBase = this.add
      .circle(92, VIEW_HEIGHT - 98, 54, 0x111714, 0.56)
      .setStrokeStyle(2, COLORS.parchmentDark, 0.7)
      .setScrollFactor(0)
      .setDepth(5000);
    this.joystickKnob = this.add
      .circle(92, VIEW_HEIGHT - 98, 23, COLORS.iron, 0.72)
      .setStrokeStyle(2, COLORS.parchment, 0.65)
      .setScrollFactor(0)
      .setDepth(5001);

    const keyboard = this.input.keyboard;
    if (keyboard) {
      this.cursors = keyboard.createCursorKeys();
      this.wasd = keyboard.addKeys({
        up: Phaser.Input.Keyboard.KeyCodes.W,
        down: Phaser.Input.Keyboard.KeyCodes.S,
        left: Phaser.Input.Keyboard.KeyCodes.A,
        right: Phaser.Input.Keyboard.KeyCodes.D,
      }) as MovementKeys;
    }

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.ended) {
        const resultAction = getResultOverlayActionAt(pointer.x, pointer.y);
        if (resultAction) this.performResultAction(resultAction);
        return;
      }
      if (this.manuallyPaused) {
        const pauseAction = getPauseOverlayActionAt(pointer.x, pointer.y);
        if (pauseAction === 'resume') this.closePause();
        else if (pauseAction === 'toggle-sound') this.toggleSoundSetting();
        else if (pauseAction === 'menu') this.scene.start('menu');
        return;
      }
      if (this.choosingUpgrade || this.ended || this.externallyPaused || this.manuallyPaused) return;
      if (pointer.x > VIEW_WIDTH * 0.62 || pointer.y < VIEW_HEIGHT * 0.45) return;

      this.joystickPointerId = pointer.id;
      const baseX = Phaser.Math.Clamp(pointer.x, 62, 150);
      const baseY = Phaser.Math.Clamp(pointer.y, VIEW_HEIGHT - 190, VIEW_HEIGHT - 62);
      this.joystickBase.setPosition(baseX, baseY);
      this.joystickKnob.setPosition(baseX, baseY);
      this.updateJoystick(pointer);
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer.id === this.joystickPointerId) this.updateJoystick(pointer);
    });

    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (this.choosingUpgrade) {
        const choiceIndex = getUpgradeChoiceIndexAt(
          pointer.x,
          pointer.y,
          this.activeUpgradeChoices.length,
          VIEW_WIDTH,
        );
        if (choiceIndex !== null) this.selectUpgrade(this.activeUpgradeChoices[choiceIndex]!);
        return;
      }
      if (pointer.id === this.joystickPointerId) this.releaseJoystick();
    });
  }

  private updateJoystick(pointer: Phaser.Input.Pointer): void {
    const delta = new Phaser.Math.Vector2(
      pointer.x - this.joystickBase.x,
      pointer.y - this.joystickBase.y,
    );
    const distance = Math.min(delta.length(), 48);
    if (delta.lengthSq() > 0) delta.normalize();

    this.joystickVector.copy(delta).scale(distance / 48);
    this.joystickKnob.setPosition(
      this.joystickBase.x + delta.x * distance,
      this.joystickBase.y + delta.y * distance,
    );
  }

  private releaseJoystick(): void {
    this.joystickPointerId = null;
    this.joystickVector.set(0, 0);
    this.joystickBase.setPosition(92, VIEW_HEIGHT - 98);
    this.joystickKnob.setPosition(92, VIEW_HEIGHT - 98);
  }

  private updateMovement(): void {
    const input = this.joystickVector.clone();

    if (this.cursors || this.wasd) {
      const left = this.cursors?.left.isDown || this.wasd?.left.isDown;
      const right = this.cursors?.right.isDown || this.wasd?.right.isDown;
      const up = this.cursors?.up.isDown || this.wasd?.up.isDown;
      const down = this.cursors?.down.isDown || this.wasd?.down.isDown;
      input.x += Number(Boolean(right)) - Number(Boolean(left));
      input.y += Number(Boolean(down)) - Number(Boolean(up));
    }

    if (input.lengthSq() > 1) input.normalize();
    this.player.setVelocity(input.x * this.stats.speed, input.y * this.stats.speed);
    this.player.setRotation(input.x * 0.055);
    if (Math.abs(input.x) > 0.08) this.player.setFlipX(input.x < 0);
    this.player.setDepth(this.player.y);
  }

  private updateHealthRecovery(deltaMs: number): void {
    const nearCampfire = CAMPFIRE_SPOTS.some((campfire) => (
      Phaser.Math.Distance.Between(this.player.x, this.player.y, campfire.x, campfire.y)
        <= campfire.radius
    ));
    const recoveryPerSecond = getRecoveryPerSecond(
      this.stats.hpRegenPerSecond,
      nearCampfire,
      CAMPFIRE_HEAL_PER_SECOND,
    );
    this.healPlayer(recoveryPerSecond * deltaMs / 1000);

    if (!this.campfireStatusText) return;
    this.campfireStatusText
      .setPosition(this.player.x, this.player.y - 78)
      .setDepth(this.player.y + 40)
      .setText(this.stats.hp >= this.stats.maxHp
        ? 'КОСТЁР · HP ПОЛНОЕ'
        : `ВОССТАНОВЛЕНИЕ +${recoveryPerSecond.toFixed(2).replace('.', ',')} HP/С`)
      .setVisible(nearCampfire);
  }

  private healPlayer(amount: number): number {
    const previousHp = this.stats.hp;
    this.stats.hp = applyHealing(this.stats.hp, this.stats.maxHp, amount);
    return this.stats.hp - previousHp;
  }

  private updateSpawning(): void {
    const seconds = this.elapsedMs / 1000;
    const interval = Math.max(230, 760 - seconds * 2.5);
    if (this.spawnAccumulatorMs < interval) return;

    this.spawnAccumulatorMs -= interval;
    const count = seconds > 120 ? 2 : 1;
    for (let index = 0; index < count; index += 1) this.spawnEnemy();
  }

  private spawnEnemy(forcedKind?: EnemyKind): void {
    if (this.enemies.countActive(true) >= 150) return;

    let x = this.player.x;
    let y = this.player.y;
    for (let attempt = 0; attempt < 10; attempt += 1) {
      const angle = Phaser.Math.FloatBetween(0, Math.PI * 2);
      const distance = Phaser.Math.Between(330, 440);
      x = Phaser.Math.Clamp(this.player.x + Math.cos(angle) * distance, 40, WORLD_WIDTH - 40);
      y = Phaser.Math.Clamp(this.player.y + Math.sin(angle) * distance, 40, WORLD_HEIGHT - 40);
      if (!isPointBlocked(x, y, 34)) break;
    }
    if (isPointBlocked(x, y, 34)) return;
    const seconds = this.elapsedMs / 1000;
    const kind = forcedKind
      ?? (Math.random() < getRangedSpawnChance(seconds) ? 'ranged' : 'melee');
    const textureKey = kind === 'ranged' ? 'plague-cultist' : 'plague-peasant';
    const enemy = this.enemies.get(x, y, textureKey) as Phaser.Physics.Arcade.Sprite | null;
    if (!enemy) return;

    enemy.enableBody(true, x, y, true, true);
    enemy.setTexture(textureKey);
    enemy.clearTint();
    enemy.setAlpha(1);
    if (kind === 'ranged') enemy.setCircle(14, 10, 25);
    else enemy.setCircle(13, 9, 22);
    enemy.setDataEnabled();
    const maxHp = kind === 'ranged' ? 27 + seconds * 0.11 : 34 + seconds * 0.13;
    enemy.setData('kind', kind);
    enemy.setData('hp', maxHp);
    enemy.setData('maxHp', maxHp);
    enemy.setData('speed', kind === 'ranged'
      ? 58 + Math.min(26, seconds * 0.12)
      : 48 + Math.min(34, seconds * 0.18));
    enemy.setData('lastHitAt', -1000);
    enemy.setData('lastShotAt', -3000);
    enemy.setData('shotCooldownMs', Phaser.Math.Between(1500, 2050));
    enemy.setData('strafeDirection', Math.random() < 0.5 ? -1 : 1);
    enemy.setDepth(y);
    this.ensureEnemyHpLabel(enemy);
    this.updateEnemyHpLabel(enemy);
  }

  private updateEnemies(time: number): void {
    const playerPosition = new Phaser.Math.Vector2(this.player.x, this.player.y);

    for (const child of this.enemies.getChildren()) {
      const enemy = child as Phaser.Physics.Arcade.Sprite;
      if (!enemy.active) continue;

      const direction = playerPosition.clone().subtract(new Phaser.Math.Vector2(enemy.x, enemy.y));
      const distance = direction.length();
      if (distance > 0) direction.normalize();
      const speed = enemy.getData('speed') as number;
      const kind = enemy.getData('kind') as EnemyKind;

      if (kind === 'ranged') {
        const movementMode = getRangedMovementMode(distance);
        if (movementMode === 'approach') {
          enemy.setVelocity(direction.x * speed, direction.y * speed);
        } else if (movementMode === 'retreat') {
          enemy.setVelocity(-direction.x * speed * 0.88, -direction.y * speed * 0.88);
        } else {
          const strafeDirection = enemy.getData('strafeDirection') as number;
          const tangent = new Phaser.Math.Vector2(-direction.y, direction.x).scale(strafeDirection);
          const distanceCorrection = Phaser.Math.Clamp((distance - 235) / 90, -0.28, 0.28);
          tangent.scale(speed * 0.58).add(direction.clone().scale(speed * distanceCorrection));
          enemy.setVelocity(tangent.x, tangent.y);
        }

        const lastShotAt = enemy.getData('lastShotAt') as number;
        const shotCooldownMs = enemy.getData('shotCooldownMs') as number;
        if (canRangedEnemyShoot(distance, time - lastShotAt, shotCooldownMs)) {
          enemy.setData('lastShotAt', time);
          this.shootEnemyProjectile(enemy, time);
        }
      } else {
        enemy.setVelocity(direction.x * speed, direction.y * speed);
        const lastHitAt = enemy.getData('lastHitAt') as number;
        if (distance < 30 && time - lastHitAt > 760) {
          enemy.setData('lastHitAt', time);
          this.damagePlayer(8, time);
        }
      }

      enemy.setFlipX(direction.x < 0);
      enemy.setDepth(enemy.y);
      this.updateEnemyHpLabel(enemy);
    }
  }

  private ensureEnemyHpLabel(enemy: Phaser.Physics.Arcade.Sprite): void {
    let hpLabel = enemy.getData('hpLabel') as Phaser.GameObjects.Text | undefined;
    if (!hpLabel || !hpLabel.scene) {
      hpLabel = this.add
        .text(enemy.x, enemy.y - 36, '', {
          fontFamily: 'Arial, sans-serif',
          fontSize: '10px',
          fontStyle: 'bold',
          color: '#e5d9bf',
          stroke: '#070908',
          strokeThickness: 3,
        })
        .setOrigin(0.5, 1);
      enemy.setData('hpLabel', hpLabel);
    }
    hpLabel.setActive(true).setVisible(true);
  }

  private updateEnemyHpLabel(enemy: Phaser.Physics.Arcade.Sprite): void {
    const hpLabel = enemy.getData('hpLabel') as Phaser.GameObjects.Text | undefined;
    if (!hpLabel) return;
    if (!enemy.active) {
      hpLabel.setVisible(false);
      return;
    }

    const hp = Math.max(0, enemy.getData('hp') as number);
    const maxHp = enemy.getData('maxHp') as number;
    const kind = enemy.getData('kind') as EnemyKind;
    hpLabel
      .setText(`HP ${Math.ceil(hp)}`)
      .setColor(hp / maxHp <= 0.3 ? '#df7770' : '#e5d9bf')
      .setPosition(enemy.x, enemy.y - (kind === 'ranged' ? 39 : 35))
      .setDepth(enemy.y + 4)
      .setVisible(true);
  }

  private shootEnemyProjectile(enemy: Phaser.Physics.Arcade.Sprite, time: number): void {
    const projectile = this.enemyProjectiles.get(
      enemy.x,
      enemy.y - 6,
      'cultist-bolt',
    ) as Phaser.Physics.Arcade.Sprite | null;
    if (!projectile) return;

    const angle = Phaser.Math.Angle.Between(enemy.x, enemy.y, this.player.x, this.player.y)
      + Phaser.Math.FloatBetween(-0.045, 0.045);
    projectile.enableBody(true, enemy.x, enemy.y - 6, true, true);
    projectile.setTexture('cultist-bolt');
    projectile.setCircle(6, 3, 3);
    projectile.setRotation(angle);
    projectile.setVelocity(
      Math.cos(angle) * RANGED_ENEMY.projectileSpeed,
      Math.sin(angle) * RANGED_ENEMY.projectileSpeed,
    );
    projectile.setDataEnabled();
    projectile.setData('expiresAt', time + 3600);
    projectile.setData('damage', RANGED_ENEMY.projectileDamage);
    projectile.setDepth(enemy.y + 3);

    enemy.setTint(0xd29861);
    this.time.delayedCall(90, () => enemy.active && enemy.clearTint());
  }

  private updateEnemyProjectiles(time: number): void {
    for (const child of this.enemyProjectiles.getChildren()) {
      const projectile = child as Phaser.Physics.Arcade.Sprite;
      if (!projectile.active) continue;

      const expiresAt = projectile.getData('expiresAt') as number;
      if (time >= expiresAt
        || projectile.x < 0
        || projectile.x > WORLD_WIDTH
        || projectile.y < 0
        || projectile.y > WORLD_HEIGHT) {
        projectile.disableBody(true, true);
        continue;
      }

      projectile.setDepth(projectile.y + 2);
      const distance = Phaser.Math.Distance.Between(
        projectile.x,
        projectile.y,
        this.player.x,
        this.player.y,
      );
      if (distance <= 24) {
        const damage = projectile.getData('damage') as number;
        projectile.disableBody(true, true);
        this.damagePlayer(damage, time);
      }
    }
  }

  private tryAttack(time: number): void {
    if (time - this.lastAttackAt < this.stats.attackCooldownMs) return;

    let nearest: Phaser.Physics.Arcade.Sprite | undefined;
    let nearestDistance = Number.POSITIVE_INFINITY;
    for (const child of this.enemies.getChildren()) {
      const enemy = child as Phaser.Physics.Arcade.Sprite;
      if (!enemy.active) continue;
      const distance = Phaser.Math.Distance.Between(this.player.x, this.player.y, enemy.x, enemy.y);
      if (distance < nearestDistance) {
        nearest = enemy;
        nearestDistance = distance;
      }
    }

    if (!nearest || nearestDistance > this.stats.attackRange * 1.18) return;
    this.lastAttackAt = time;

    const attackAngle = Phaser.Math.Angle.Between(this.player.x, this.player.y, nearest.x, nearest.y);
    this.player.setFlipX(nearest.x < this.player.x);
    const halfArc = Math.PI * 0.48;
    this.playSwordWhoosh();
    this.drawSwordArc(attackAngle, halfArc);

    for (const child of this.enemies.getChildren()) {
      const enemy = child as Phaser.Physics.Arcade.Sprite;
      if (!enemy.active) continue;
      const distance = Phaser.Math.Distance.Between(this.player.x, this.player.y, enemy.x, enemy.y);
      if (distance > this.stats.attackRange) continue;

      const targetAngle = Phaser.Math.Angle.Between(this.player.x, this.player.y, enemy.x, enemy.y);
      const difference = Math.abs(Phaser.Math.Angle.Wrap(targetAngle - attackAngle));
      if (difference <= halfArc) this.damageEnemy(enemy, attackAngle);
    }
  }

  private drawSwordArc(angle: number, halfArc: number): void {
    const slash = this.add.graphics({ x: this.player.x, y: this.player.y });
    slash.fillStyle(COLORS.holy, 0.28);
    slash.lineStyle(4, COLORS.parchment, 0.8);
    slash.beginPath();
    slash.moveTo(0, 0);
    slash.arc(0, 0, this.stats.attackRange, angle - halfArc, angle + halfArc, false);
    slash.closePath();
    slash.fillPath();
    slash.strokePath();
    slash.setDepth(this.player.y + 1);

    this.tweens.add({
      targets: slash,
      alpha: 0,
      scale: 1.08,
      duration: 170,
      ease: 'Quad.easeOut',
      onComplete: () => slash.destroy(),
    });
  }

  private playSwordWhoosh(): void {
    if (!this.soundEnabled || this.sound.mute) return;

    const soundManager = this.sound as Phaser.Sound.WebAudioSoundManager;
    const context = soundManager.context;
    if (!context || context.state === 'closed') return;
    if (context.state === 'suspended') {
      void context.resume();
      return;
    }

    const duration = 0.19;
    if (!this.swordWhooshBuffer || this.swordWhooshBuffer.sampleRate !== context.sampleRate) {
      const frameCount = Math.floor(context.sampleRate * duration);
      const buffer = context.createBuffer(1, frameCount, context.sampleRate);
      const samples = buffer.getChannelData(0);
      for (let index = 0; index < frameCount; index += 1) {
        const progress = index / frameCount;
        const envelope = Math.sin(Math.PI * progress) ** 1.7;
        samples[index] = (Math.random() * 2 - 1) * envelope;
      }
      this.swordWhooshBuffer = buffer;
    }

    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    const now = context.currentTime;
    source.buffer = this.swordWhooshBuffer;
    source.playbackRate.value = Phaser.Math.FloatBetween(0.9, 1.08);
    filter.type = 'bandpass';
    filter.Q.value = 0.78;
    filter.frequency.setValueAtTime(1450, now);
    filter.frequency.exponentialRampToValueAtTime(380, now + duration);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.13, now + 0.035);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    source.connect(filter).connect(gain).connect(context.destination);
    source.start(now);
    source.stop(now + duration);
  }

  private damageEnemy(enemy: Phaser.Physics.Arcade.Sprite, attackAngle: number): void {
    const previousHp = Math.max(0, enemy.getData('hp') as number);
    const damageDealt = Math.min(previousHp, this.stats.damage);
    const hp = previousHp - this.stats.damage;
    enemy.setData('hp', hp);
    this.healPlayer(getVampirismHealing(damageDealt, this.stats.vampirism));
    enemy.setTintFill(0xe5c8a5);
    this.time.delayedCall(70, () => enemy.active && enemy.clearTint());
    enemy.x += Math.cos(attackAngle) * 12;
    enemy.y += Math.sin(attackAngle) * 12;

    if (hp <= 0) this.killEnemy(enemy);
    else this.updateEnemyHpLabel(enemy);
  }

  private killEnemy(enemy: Phaser.Physics.Arcade.Sprite): void {
    this.kills += 1;
    const x = enemy.x;
    const y = enemy.y;
    const hpLabel = enemy.getData('hpLabel') as Phaser.GameObjects.Text | undefined;
    hpLabel?.setVisible(false);
    enemy.disableBody(true, true);

    const shard = this.shards.get(x, y, 'xp-shard') as Phaser.Physics.Arcade.Sprite | null;
    if (shard) {
      shard.enableBody(true, x, y, true, true);
      shard.setDepth(y - 1);
      shard.setDataEnabled();
      shard.setData('value', 1);
    }

    const burst = this.add.circle(x, y, 12, COLORS.blood, 0.45).setDepth(y + 2);
    this.tweens.add({
      targets: burst,
      radius: 28,
      alpha: 0,
      duration: 230,
      onComplete: () => burst.destroy(),
    });
  }

  private updateShards(): void {
    for (const child of this.shards.getChildren()) {
      const shard = child as Phaser.Physics.Arcade.Sprite;
      if (!shard.active) continue;

      const distance = Phaser.Math.Distance.Between(this.player.x, this.player.y, shard.x, shard.y);
      if (distance <= this.stats.magnetRadius) {
        this.physics.moveToObject(shard, this.player, 235 + this.stats.magnetRadius * 0.45);
      } else {
        shard.setVelocity(0, 0);
      }

      if (distance <= 25) this.collectShard(shard);
    }
  }

  private collectShard(shard: Phaser.Physics.Arcade.Sprite): void {
    this.xp += shard.getData('value') as number;
    shard.disableBody(true, true);
    if (this.xp >= this.xpToNext) this.gainLevel();
  }

  private gainLevel(): void {
    this.xp -= this.xpToNext;
    this.level += 1;
    this.xpToNext = Math.floor(5 + this.level * 3.5);
    this.showUpgradeChoices();
  }

  private showUpgradeChoices(): void {
    const choices = chooseUpgradeOffers(this.upgradeRanks, 3);
    if (choices.length === 0) return;

    this.activeUpgradeChoices = choices;
    this.choosingUpgrade = true;
    this.physics.world.pause();
    this.player.setVelocity(0, 0);
    this.releaseJoystick();

    const overlay = this.add.container(0, 0).setScrollFactor(0).setDepth(8000);
    overlay.add(
      this.add
        .rectangle(0, 0, VIEW_WIDTH, VIEW_HEIGHT, 0x050706, 0.91)
        .setOrigin(0)
        .setStrokeStyle(2, 0x3e3529, 0.8),
    );
    overlay.add(
      this.add
        .text(VIEW_WIDTH / 2, 68, 'НОВЫЙ УРОВЕНЬ', {
          fontFamily: 'Georgia, serif',
          fontSize: '27px',
          color: '#eadbb3',
          stroke: '#15100b',
          strokeThickness: 5,
        })
        .setOrigin(0.5),
    );
    overlay.add(
      this.add
        .text(VIEW_WIDTH / 2, 104, `Уровень ${this.level} · выбери усиление`, {
          fontSize: '15px',
          color: '#9f947c',
        })
        .setOrigin(0.5),
    );

    choices.forEach((upgrade, index) => {
      const card = this.createUpgradeCard(upgrade, index);
      overlay.add(card);
    });

    this.upgradeOverlay = overlay;
  }

  private createUpgradeCard(
    offer: UpgradeOffer,
    index: number,
  ): Phaser.GameObjects.Container {
    const { upgrade, tier } = offer;
    const tierDefinition = getTierDefinition(tier);
    const x = getUpgradeCardCenterX(index, this.activeUpgradeChoices.length, VIEW_WIDTH);
    const isDullTier = tier === 'D';
    const frameColor = isDullTier ? 0x5f625f : tierDefinition.accent;
    const statColor = isDullTier ? '#b4b7b3' : '#f3dda0';
    const card = this.add
      .container(x, UPGRADE_CARD_CENTER_Y)
      .setSize(UPGRADE_CARD_WIDTH, UPGRADE_CARD_HEIGHT);
    const shadow = this.add.rectangle(4, 7, UPGRADE_CARD_WIDTH, UPGRADE_CARD_HEIGHT, 0x000000, 0.68);
    const background = this.add
      .rectangle(0, 0, UPGRADE_CARD_WIDTH, UPGRADE_CARD_HEIGHT, 0x121512, 1)
      .setStrokeStyle(4, frameColor, 0.96);
    const tierGlow = this.add.rectangle(
      0,
      0,
      UPGRADE_CARD_WIDTH - 10,
      UPGRADE_CARD_HEIGHT - 10,
      frameColor,
      isDullTier ? 0.035 : 0.075,
    );
    const innerFrame = this.add
      .rectangle(0, 0, UPGRADE_CARD_WIDTH - 10, UPGRADE_CARD_HEIGHT - 10, 0x000000, 0)
      .setStrokeStyle(1, isDullTier ? 0x777a76 : COLORS.parchmentDark, 0.72);
    const iconWell = this.add
      .rectangle(0, -85, 96, 96, 0x090c0a, 1)
      .setStrokeStyle(4, frameColor, isDullTier ? 0.74 : 0.96);
    const iconInner = this.add
      .rectangle(0, -85, 82, 82, upgrade.accent, isDullTier ? 0.035 : 0.11)
      .setStrokeStyle(1, isDullTier ? 0x777a76 : 0xc8b784, 0.48);
    const icon = this.createUpgradeIcon(upgrade.icon, 0, -85, frameColor, isDullTier);
    const divider = this.add.rectangle(0, 66, UPGRADE_CARD_WIDTH - 30, 2, frameColor, 0.72);
    const ornament = this.add.graphics();
    ornament.lineStyle(2, frameColor, 0.9);
    ornament.lineBetween(-60, -198, -45, -198);
    ornament.lineBetween(-60, -198, -60, -183);
    ornament.lineBetween(60, -198, 45, -198);
    ornament.lineBetween(60, -198, 60, -183);
    ornament.lineBetween(-60, 198, -45, 198);
    ornament.lineBetween(-60, 198, -60, 183);
    ornament.lineBetween(60, 198, 45, 198);
    ornament.lineBetween(60, 198, 60, 183);
    ornament.fillStyle(frameColor, 1);
    ornament.fillCircle(-60, -198, 3).fillCircle(60, -198, 3);
    ornament.fillCircle(-60, 198, 3).fillCircle(60, 198, 3);

    const tierSeal = this.add
      .rectangle(0, -178, 64, 42, frameColor, 0.98)
      .setStrokeStyle(2, isDullTier ? 0x9b9d99 : COLORS.parchment, 0.82);
    const tierText = this.add
      .text(0, -178, tier, {
        fontFamily: 'Georgia, serif',
        fontSize: tier.length > 1 ? '21px' : '25px',
        color: isDullTier ? '#deded8' : '#16120e',
        fontStyle: 'bold',
        stroke: isDullTier ? '#2b2d2b' : undefined,
        strokeThickness: isDullTier ? 2 : 0,
      })
      .setOrigin(0.5);
    const rank = this.upgradeRanks[upgrade.id] ?? 0;
    const category = this.add
      .text(0, -22, `СТАТ · ${upgrade.badgeLabel}`, {
        fontSize: '10px',
        fontStyle: 'bold',
        color: isDullTier ? '#8d908c' : '#9f947c',
        letterSpacing: 1,
        align: 'center',
        wordWrap: { width: UPGRADE_CARD_WIDTH - 12 },
      })
      .setOrigin(0.5);
    const title = this.add
      .text(0, 16, upgrade.title.toUpperCase(), {
        fontFamily: 'Georgia, serif',
        fontSize: '12px',
        color: isDullTier ? '#9c9f9b' : '#e6d5aa',
        fontStyle: 'bold',
        align: 'center',
        wordWrap: { width: UPGRADE_CARD_WIDTH - 12 },
      })
      .setOrigin(0.5);
    const description = this.add
      .text(0, 49, upgrade.description, {
        fontSize: '9px',
        color: isDullTier ? '#777a76' : '#827968',
        align: 'center',
        wordWrap: { width: UPGRADE_CARD_WIDTH - 18 },
      })
      .setOrigin(0.5);
    const changes = describeUpgradeChanges(this.stats, upgrade, tier).map(splitUpgradeChange);
    const statObjects: Phaser.GameObjects.GameObject[] = [];
    const statStartY = changes.length > 1 ? 86 : 112;
    changes.forEach((change, changeIndex) => {
      const rowY = statStartY + changeIndex * 46;
      statObjects.push(
        this.add
          .text(0, rowY, change.label, {
            fontSize: '9px',
            color: isDullTier ? '#777a76' : '#8e8573',
            letterSpacing: 1,
          })
          .setOrigin(0.5),
        this.add
          .text(0, rowY + 17, change.value, {
            fontFamily: 'Arial, sans-serif',
            fontSize: '14px',
            fontStyle: 'bold',
            color: statColor,
            stroke: '#070908',
            strokeThickness: 3,
            align: 'center',
          })
          .setOrigin(0.5),
      );
    });
    const rankText = this.add
      .text(0, 184, rank > 0
        ? `ВЗЯТО ×${rank}  •  РАНГ ${rank + 1}/${upgrade.maxRank}`
        : `НЕ ВЗЯТО  •  РАНГ 1/${upgrade.maxRank}`, {
        fontSize: '9px',
        fontStyle: 'bold',
        color: isDullTier ? '#858884' : '#b3a57f',
      })
      .setOrigin(0.5);

    card
      .setInteractive(
        new Phaser.Geom.Rectangle(
          -UPGRADE_CARD_WIDTH / 2,
          -UPGRADE_CARD_HEIGHT / 2,
          UPGRADE_CARD_WIDTH,
          UPGRADE_CARD_HEIGHT,
        ),
        Phaser.Geom.Rectangle.Contains,
      )
      .on('pointerover', () => {
        background.setFillStyle(0x27241d, 1).setStrokeStyle(5, frameColor, 1);
        card.setScale(1.015);
      })
      .on('pointerout', () => {
        background.setFillStyle(0x121512, 1).setStrokeStyle(4, frameColor, 0.96);
        card.setScale(1);
      })
      .on('pointerup', () => this.selectUpgrade(offer));
    if (card.input) card.input.cursor = 'pointer';

    card.add([
      shadow,
      background,
      tierGlow,
      innerFrame,
      iconWell,
      iconInner,
      icon,
      divider,
      ornament,
      tierSeal,
      tierText,
      category,
      title,
      description,
      ...statObjects,
      rankText,
    ]);
    return card;
  }

  private createUpgradeIcon(
    icon: UpgradeIcon,
    x: number,
    y: number,
    color: number,
    muted: boolean,
  ): Phaser.GameObjects.Graphics {
    const graphic = this.add.graphics({ x, y });
    const primary = muted ? 0xa2a5a1 : 0xe8d9b2;
    const secondary = muted ? 0x696c69 : color;
    const dark = 0x0a0c0b;

    if (icon === 'sword') {
      graphic.fillStyle(dark, 1).fillPoints([
        { x: 0, y: -42 }, { x: 13, y: -26 }, { x: 9, y: 19 }, { x: -9, y: 19 }, { x: -13, y: -26 },
      ], true);
      graphic.fillStyle(primary, 1).fillPoints([
        { x: 0, y: -35 }, { x: 8, y: -23 }, { x: 5, y: 15 }, { x: -5, y: 15 }, { x: -8, y: -23 },
      ], true);
      graphic.lineStyle(3, secondary, 0.94).lineBetween(0, -31, 0, 14);
      graphic.fillStyle(dark, 1).fillTriangle(-9, -10, -18, -5, -8, 1);
      graphic.fillTriangle(8, 1, 18, 6, 7, 11);
      graphic.fillStyle(dark, 1).fillRoundedRect(-25, 14, 50, 9, 3);
      graphic.fillStyle(secondary, 1).fillRoundedRect(-20, 16, 40, 5, 2);
      graphic.fillStyle(0x4c3325, 1).fillRoundedRect(-5, 22, 10, 23, 3);
      graphic.lineStyle(2, primary, 0.7).lineBetween(-3, 25, 3, 40);
      graphic.fillStyle(secondary, 1).fillCircle(0, 46, 6);
    } else if (icon === 'rapid-blades') {
      graphic.lineStyle(7, dark, 1).lineBetween(-9, 29, 25, -27);
      graphic.lineStyle(3, primary, 1).lineBetween(-6, 26, 25, -27);
      graphic.fillStyle(primary, 1).fillTriangle(31, -38, 18, -28, 27, -20);
      graphic.lineStyle(8, dark, 1).lineBetween(-17, 18, 4, 31);
      graphic.lineStyle(4, secondary, 1).lineBetween(-16, 17, 3, 29);
      graphic.lineStyle(6, 0x4c3325, 1).lineBetween(-10, 28, -17, 41);
      graphic.lineStyle(5, secondary, 0.98).lineBetween(-39, -23, -7, -23);
      graphic.lineBetween(-44, -8, -15, -8);
      graphic.lineBetween(-39, 7, -20, 7);
      graphic.fillStyle(secondary, 0.9).fillTriangle(-4, -28, 8, -23, -4, -18);
    } else if (icon === 'boot') {
      graphic.fillStyle(dark, 1).fillPoints([
        { x: -10, y: -37 }, { x: 14, y: -37 }, { x: 14, y: 10 }, { x: 39, y: 25 }, { x: 34, y: 39 },
        { x: -16, y: 39 }, { x: -19, y: 27 }, { x: -10, y: 12 },
      ], true);
      graphic.fillStyle(primary, 1).fillPoints([
        { x: -5, y: -31 }, { x: 9, y: -31 }, { x: 9, y: 14 }, { x: 32, y: 27 }, { x: 28, y: 33 },
        { x: -11, y: 33 }, { x: -13, y: 27 }, { x: -5, y: 10 },
      ], true);
      graphic.lineStyle(4, secondary, 0.9).lineBetween(-3, -17, 9, -17);
      graphic.lineBetween(-3, -6, 9, -6);
      graphic.lineStyle(5, secondary, 0.9).lineBetween(-42, -14, -19, -14);
      graphic.lineBetween(-47, 1, -19, 1);
      graphic.lineBetween(-39, 16, -16, 16);
      graphic.fillStyle(secondary, 0.92).fillTriangle(-17, -18, -29, -27, -20, -7);
    } else if (icon === 'shield') {
      graphic.fillStyle(dark, 1).fillPoints([
        { x: 0, y: -42 }, { x: 34, y: -28 }, { x: 29, y: 17 },
        { x: 0, y: 43 }, { x: -29, y: 17 }, { x: -34, y: -28 },
      ], true);
      graphic.fillStyle(primary, 1).fillPoints([
        { x: 0, y: -35 }, { x: 27, y: -24 }, { x: 23, y: 13 },
        { x: 0, y: 35 }, { x: -23, y: 13 }, { x: -27, y: -24 },
      ], true);
      graphic.lineStyle(3, secondary, 0.9).lineBetween(-22, -21, 0, -29);
      graphic.fillStyle(secondary, 1).fillRoundedRect(-5, -19, 10, 38, 2);
      graphic.fillRoundedRect(-18, -6, 36, 10, 2);
      graphic.lineStyle(2, dark, 0.35).lineBetween(0, -31, 0, 31);
    } else {
      graphic.lineStyle(21, dark, 1);
      graphic.beginPath().arc(0, 2, 27, 0.12, Math.PI - 0.12, false).strokePath();
      graphic.lineStyle(12, primary, 1);
      graphic.beginPath().arc(0, 2, 27, 0.12, Math.PI - 0.12, false).strokePath();
      graphic.fillStyle(secondary, 1).fillRoundedRect(20, 0, 14, 23, 3);
      graphic.fillRoundedRect(-34, 0, 14, 23, 3);
      graphic.fillStyle(primary, 1).fillPoints([
        { x: 0, y: -43 }, { x: 10, y: -31 }, { x: 0, y: -18 }, { x: -10, y: -31 },
      ], true);
      graphic.lineStyle(3, secondary, 0.86).lineBetween(0, -16, 0, -4);
      graphic.lineBetween(-18, -25, -10, -12);
      graphic.lineBetween(18, -25, 10, -12);
    }

    return graphic;
  }

  private selectUpgrade(offer: UpgradeOffer): void {
    if (!this.choosingUpgrade) return;

    const { upgrade, tier } = offer;
    const changes = describeUpgradeChanges(this.stats, upgrade, tier);
    const result = applyUpgrade(this.stats, this.upgradeRanks, upgrade, tier);
    this.stats = result.stats;
    this.upgradeRanks = result.ranks;
    this.activeUpgradeChoices = [];
    this.upgradeOverlay?.destroy(true);
    this.upgradeOverlay = undefined;
    this.choosingUpgrade = false;
    this.physics.world.resume();
    this.updateHud();
    this.refreshTakenUpgradesHud();
    this.showUpgradeToast(`${upgrade.title} · ${tier}`, changes);

    if (this.xp >= this.xpToNext) this.time.delayedCall(80, () => this.gainLevel());
  }

  private showUpgradeToast(title: string, changes: string[]): void {
    this.upgradeToast?.destroy(true);

    const toast = this.add.container(VIEW_WIDTH / 2, 151).setScrollFactor(0).setDepth(7000);
    const background = this.add
      .rectangle(0, 0, VIEW_WIDTH - 42, 66, 0x171511, 0.94)
      .setStrokeStyle(2, COLORS.parchmentDark, 0.82);
    const titleText = this.add
      .text(0, -15, title, {
        fontFamily: 'Georgia, serif',
        fontSize: '15px',
        color: '#eadbb3',
      })
      .setOrigin(0.5);
    const changesText = this.add
      .text(0, 13, changes.join('  •  '), {
        fontSize: '12px',
        color: '#e7cf91',
        align: 'center',
        wordWrap: { width: VIEW_WIDTH - 62 },
      })
      .setOrigin(0.5);

    toast.add([background, titleText, changesText]);
    this.upgradeToast = toast;
    this.tweens.add({
      targets: toast,
      y: 141,
      alpha: 0,
      delay: 1200,
      duration: 420,
      ease: 'Quad.easeIn',
      onComplete: () => {
        if (this.upgradeToast === toast) this.upgradeToast = undefined;
        toast.destroy(true);
      },
    });
  }

  private damagePlayer(rawDamage: number, time: number): void {
    if (time < this.damageInvulnerableUntil) return;
    this.damageInvulnerableUntil = time + 180;

    const damage = Math.max(1, rawDamage * (1 - this.stats.armor));
    this.stats.hp = Math.max(0, this.stats.hp - damage);
    this.player.setTintFill(0xc45852);
    this.cameras.main.shake(70, 0.0024);
    this.time.delayedCall(90, () => this.player.active && this.player.clearTint());
    if (this.stats.hp <= 0) this.endRun(false);
  }

  private createHud(): void {
    const panel = this.add
      .rectangle(VIEW_WIDTH / 2, 46, VIEW_WIDTH - 24, 76, 0x0a0d0c, 0.78)
      .setStrokeStyle(1, COLORS.parchmentDark, 0.48)
      .setScrollFactor(0)
      .setDepth(6000);
    panel.setOrigin(0.5);

    this.xpBar = this.add.graphics().setScrollFactor(0).setDepth(6001);
    this.hpBar = this.add.graphics().setScrollFactor(0).setDepth(6001);
    this.levelText = this.add
      .text(20, 29, 'УР. 1', { fontSize: '15px', color: '#ded0ac', fontStyle: 'bold' })
      .setScrollFactor(0)
      .setDepth(6002);
    this.timerText = this.add
      .text(VIEW_WIDTH / 2, 29, '03:00', {
        fontFamily: 'Georgia, serif',
        fontSize: '20px',
        color: '#eee0b8',
      })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setDepth(6002);
    this.killText = this.add
      .text(VIEW_WIDTH - 20, 31, '☠ 0', { fontSize: '15px', color: '#b8ae96' })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(6002);
    this.fpsText = this.add
      .text(212, 70, '60 FPS', { fontSize: '10px', color: '#6f786d' })
      .setOrigin(0, 0.5)
      .setScrollFactor(0)
      .setDepth(6002);
    this.hpText = this.add
      .text(107, 63, 'HP 100 / 100', {
        fontSize: '10px',
        color: '#f1ded0',
        fontStyle: 'bold',
        stroke: '#160c0d',
        strokeThickness: 2,
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(6002);

    const pauseButton = this.add
      .rectangle(VIEW_WIDTH - 70, 67, 112, 36, 0x292c26, 0.98)
      .setStrokeStyle(2, COLORS.parchmentDark, 0.88)
      .setScrollFactor(0)
      .setDepth(6003)
      .setInteractive({ useHandCursor: true });
    const pauseIcon = this.add
      .text(VIEW_WIDTH - 70, 67, 'Ⅱ  ПАУЗА', {
        fontFamily: 'Georgia, serif',
        fontSize: '13px',
        fontStyle: 'bold',
        color: '#eadbb3',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(6004);
    pauseButton.on('pointerover', () => pauseButton.setFillStyle(0x45483d, 1));
    pauseButton.on('pointerout', () => pauseButton.setFillStyle(0x292c26, 0.98));
    pauseButton.on('pointerdown', () => this.togglePause());

    this.takenUpgradesHud = this.add
      .container(VIEW_WIDTH / 2, 101)
      .setScrollFactor(0)
      .setDepth(6002);
    this.refreshTakenUpgradesHud();

    this.campfireStatusText = this.add
      .text(this.player.x, this.player.y - 78, '', {
        fontSize: '11px',
        fontStyle: 'bold',
        color: '#f0c67a',
        stroke: '#100b08',
        strokeThickness: 4,
        letterSpacing: 1,
      })
      .setOrigin(0.5)
      .setVisible(false);

    this.createEquipmentHud();

    this.add
      .rectangle(VIEW_WIDTH / 2, VIEW_HEIGHT - 2, VIEW_WIDTH, 4, COLORS.burgundy, 0.65)
      .setScrollFactor(0)
      .setDepth(6000);
    this.updateHud();
  }

  private refreshTakenUpgradesHud(): void {
    if (!this.takenUpgradesHud) return;
    this.takenUpgradesHud.removeAll(true);
    const badges = getTakenUpgradeBadges(this.upgradeRanks);
    const panel = this.add
      .rectangle(0, 0, VIEW_WIDTH - 24, 28, 0x090b0a, 0.82)
      .setStrokeStyle(1, 0x514a3c, 0.54);
    const heading = this.add
      .text(-199, 0, 'ВЗЯТО', {
        fontSize: '8px',
        fontStyle: 'bold',
        color: '#847b6a',
        letterSpacing: 1,
      })
      .setOrigin(0, 0.5);
    this.takenUpgradesHud.add([panel, heading]);

    if (badges.length === 0) {
      this.takenUpgradesHud.add(
        this.add
          .text(-143, 0, 'УСИЛЕНИЙ ПОКА НЕТ', {
            fontSize: '9px',
            color: '#5f655f',
            letterSpacing: 1,
          })
          .setOrigin(0, 0.5),
      );
      return;
    }

    const chipWidth = Math.min(66, 342 / badges.length - 4);
    const totalWidth = badges.length * chipWidth + (badges.length - 1) * 4;
    const startX = -142 + chipWidth / 2 + Math.max(0, (342 - totalWidth) / 2);
    badges.forEach((badge, index) => {
      const x = startX + index * (chipWidth + 4);
      const chip = this.add
        .rectangle(x, 0, chipWidth, 20, badge.accent, 0.22)
        .setStrokeStyle(1, badge.accent, 0.84);
      const text = this.add
        .text(x, 0, `${badge.label} ×${badge.rank}`, {
          fontSize: '9px',
          fontStyle: 'bold',
          color: '#e0d4b6',
          stroke: '#080a09',
          strokeThickness: 2,
        })
        .setOrigin(0.5);
      this.takenUpgradesHud.add([chip, text]);
    });
  }

  private createEquipmentHud(): void {
    const equipment = this.add
      .container(VIEW_WIDTH - 92, VIEW_HEIGHT - 59)
      .setScrollFactor(0)
      .setDepth(6003);
    const label = this.add
      .text(52, -43, 'СНАРЯЖЕНИЕ', {
        fontSize: '9px',
        fontStyle: 'bold',
        color: '#8f8878',
        letterSpacing: 1,
      })
      .setOrigin(1, 0.5);

    const slotPositions = [-52, 0, 52];
    const slots = slotPositions.map((x, index) => {
      const empty = index === 2;
      return this.add
        .rectangle(x, 0, 46, 46, empty ? 0x0a0d0c : 0x171a17, 0.9)
        .setStrokeStyle(2, empty ? 0x4d524e : COLORS.parchmentDark, empty ? 0.62 : 0.88);
    });
    const icon = this.add.graphics();

    // Тяжёлый меч в первом слоте.
    icon.lineStyle(9, 0x202522, 1).lineBetween(-65, 13, -40, -14);
    icon.lineStyle(5, 0x888e88, 1).lineBetween(-64, 11, -41, -13);
    icon.lineStyle(1, 0xd1c9b3, 0.8).lineBetween(-61, 8, -42, -12);
    icon.fillStyle(0x9b9f98, 1).fillTriangle(-35, -20, -43, -11, -40, -6);
    icon.lineStyle(5, 0xa28b62, 1).lineBetween(-70, 5, -57, 17);
    icon.lineStyle(4, 0x563828, 1).lineBetween(-66, 13, -74, 21);

    // Нагрудник во втором слоте — именно броня, не второй щит.
    icon.fillStyle(0x252a27, 1).fillPoints([
      { x: -14, y: -15 }, { x: -5, y: -20 }, { x: 0, y: -13 },
      { x: 5, y: -20 }, { x: 14, y: -15 }, { x: 12, y: 17 },
      { x: 0, y: 21 }, { x: -12, y: 17 },
    ], true);
    icon.fillStyle(0x666c67, 1).fillPoints([
      { x: -10, y: -12 }, { x: -4, y: -16 }, { x: 0, y: -9 },
      { x: 4, y: -16 }, { x: 10, y: -12 }, { x: 8, y: 13 },
      { x: 0, y: 17 }, { x: -8, y: 13 },
    ], true);
    icon.lineStyle(2, 0xa5a59a, 0.72).lineBetween(0, -8, 0, 14);
    icon.lineStyle(2, 0x3a211f, 0.9).lineBetween(-8, 1, 8, 6);

    // Третий слот намеренно пустой, только с едва заметным внутренним контуром.
    const emptyInset = this.add
      .rectangle(52, 0, 34, 34, 0x000000, 0)
      .setStrokeStyle(1, 0x626762, 0.24);

    equipment.add([label, ...slots, icon, emptyInset]);
  }

  private updateHud(): void {
    if (!this.xpBar || !this.hpBar) return;

    const xpRatio = Phaser.Math.Clamp(this.xp / this.xpToNext, 0, 1);
    this.xpBar.clear();
    this.xpBar.fillStyle(0x191e1b, 1).fillRoundedRect(18, 12, VIEW_WIDTH - 36, 9, 4);
    this.xpBar.fillStyle(COLORS.xp, 1).fillRoundedRect(18, 12, (VIEW_WIDTH - 36) * xpRatio, 9, 4);

    const hpRatio = Phaser.Math.Clamp(this.stats.hp / this.stats.maxHp, 0, 1);
    this.hpBar.clear();
    this.hpBar.fillStyle(0x1f1717, 1).fillRoundedRect(18, 58, 178, 10, 4);
    this.hpBar.fillStyle(COLORS.blood, 1).fillRoundedRect(18, 58, 178 * hpRatio, 10, 4);

    const remaining = Math.max(0, PROTOTYPE_DURATION_SECONDS - Math.floor(this.elapsedMs / 1000));
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    this.timerText?.setText(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    this.levelText?.setText(`УР. ${this.level}`);
    this.killText?.setText(`☠ ${this.kills}`);
    this.fpsText?.setText(`${Math.round(this.game.loop.actualFps || 0)} FPS`);
    this.hpText?.setText(`HP ${Math.ceil(this.stats.hp)} / ${Math.round(this.stats.maxHp)}`);
  }

  private loadSoundSetting(): void {
    try {
      this.soundEnabled = window.localStorage.getItem(SOUND_SETTING_KEY) !== 'false';
    } catch {
      this.soundEnabled = true;
    }
    this.sound.setMute(!this.soundEnabled);
  }

  private saveSoundSetting(): void {
    try {
      window.localStorage.setItem(SOUND_SETTING_KEY, String(this.soundEnabled));
    } catch {
      // Настройка остаётся рабочей до закрытия вкладки, даже если хранилище недоступно.
    }
  }

  private toggleSoundSetting(): void {
    this.soundEnabled = !this.soundEnabled;
    this.sound.setMute(!this.soundEnabled);
    this.saveSoundSetting();
    this.pauseSoundButtonText
      ?.setText(this.soundEnabled ? 'ЗВУК: ВКЛ' : 'ЗВУК: ВЫКЛ')
      .setColor(this.soundEnabled ? '#d8c9a5' : '#777b77');
  }

  private togglePause(): void {
    if (this.manuallyPaused) {
      this.closePause();
      return;
    }
    if (this.ended || this.choosingUpgrade || this.externallyPaused) return;
    this.openPause();
  }

  private openPause(): void {
    if (this.manuallyPaused) return;
    this.manuallyPaused = true;
    this.physics.world.pause();
    this.player.setVelocity(0, 0);
    this.releaseJoystick();

    const overlay = this.add.container(0, 0).setScrollFactor(0).setDepth(8500);
    const veil = this.add
      .rectangle(0, 0, VIEW_WIDTH, VIEW_HEIGHT, 0x030504, 0.9)
      .setOrigin(0);
    const panel = this.add
      .rectangle(VIEW_WIDTH / 2, VIEW_HEIGHT / 2, VIEW_WIDTH - 34, VIEW_HEIGHT - 60, 0x101310, 0.98)
      .setStrokeStyle(3, COLORS.parchmentDark, 0.78);
    const innerPanel = this.add
      .rectangle(VIEW_WIDTH / 2, VIEW_HEIGHT / 2, VIEW_WIDTH - 48, VIEW_HEIGHT - 74, 0x000000, 0)
      .setStrokeStyle(1, 0x4d493d, 0.68);

    const sigil = this.add.graphics({ x: VIEW_WIDTH / 2, y: 188 });
    sigil.lineStyle(2, 0x8c7957, 0.32).strokeCircle(0, 0, 91);
    sigil.lineStyle(1, 0xd4c49c, 0.13).strokeCircle(0, 0, 74);
    for (let angle = 0; angle < 360; angle += 30) {
      const radians = Phaser.Math.DegToRad(angle);
      sigil.lineBetween(
        Math.cos(radians) * 77,
        Math.sin(radians) * 77,
        Math.cos(radians) * 89,
        Math.sin(radians) * 89,
      );
    }
    sigil.lineStyle(4, 0x706b5d, 0.42).lineBetween(0, -63, 0, 63);
    sigil.lineStyle(2, 0x8a433f, 0.54).lineBetween(-31, -20, 34, 28);

    const title = this.add
      .text(VIEW_WIDTH / 2, 60, 'ПАУЗА', {
        fontFamily: 'Georgia, serif',
        fontSize: '34px',
        fontStyle: 'bold',
        color: '#eadbb3',
        stroke: '#17110c',
        strokeThickness: 5,
        letterSpacing: 5,
      })
      .setOrigin(0.5);
    const hero = this.add.image(VIEW_WIDTH / 2, 188, HERO_PAUSE_TEXTURE_KEY).setScale(1.3);
    const heroName = this.add
      .text(VIEW_WIDTH / 2, 296, 'ИЗГНАННЫЙ РЫЦАРЬ', {
        fontFamily: 'Georgia, serif',
        fontSize: '17px',
        fontStyle: 'bold',
        color: '#d8c9a5',
        letterSpacing: 2,
      })
      .setOrigin(0.5);
    const runState = this.add
      .text(
        VIEW_WIDTH / 2,
        326,
        `УРОВЕНЬ ${this.level}  •  УБИТО ${this.kills}`,
        {
          fontSize: '11px',
          fontStyle: 'bold',
          color: '#8e8777',
          align: 'center',
        },
      )
      .setOrigin(0.5);

    const statsPanel = this.add
      .rectangle(VIEW_WIDTH / 2, 410, VIEW_WIDTH - 76, 126, 0x090c0a, 0.92)
      .setStrokeStyle(2, 0x665c49, 0.7);
    const statsTitle = this.add
      .text(VIEW_WIDTH / 2, 360, 'ХАРАКТЕРИСТИКИ', {
        fontFamily: 'Georgia, serif',
        fontSize: '12px',
        fontStyle: 'bold',
        color: '#b9aa87',
        letterSpacing: 2,
      })
      .setOrigin(0.5);
    const statsDivider = this.add
      .rectangle(VIEW_WIDTH / 2, 418, 1, 78, 0x716750, 0.44);
    const statObjects: Phaser.GameObjects.GameObject[] = [];
    const addStatRow = (
      labelX: number,
      valueX: number,
      y: number,
      label: string,
      value: string,
    ): void => {
      statObjects.push(
        this.add
          .text(labelX, y, label, {
            fontSize: '9px',
            fontStyle: 'bold',
            color: '#777669',
            letterSpacing: 1,
          })
          .setOrigin(0, 0.5),
        this.add
          .text(valueX, y, value, {
            fontSize: '12px',
            fontStyle: 'bold',
            color: '#dfd1ad',
            stroke: '#070908',
            strokeThickness: 2,
          })
          .setOrigin(1, 0.5),
      );
    };
    const attacksPerSecond = (1000 / this.stats.attackCooldownMs).toFixed(2).replace('.', ',');
    addStatRow(58, 206, 388, 'УРОН', `${Math.round(this.stats.damage)}`);
    addStatRow(58, 206, 410, 'АТАК/С', attacksPerSecond);
    addStatRow(58, 206, 432, 'СКОРОСТЬ', `${Math.round(this.stats.speed)}`);
    addStatRow(58, 206, 454, 'СБОР ОПЫТА', `${Math.round(this.stats.magnetRadius)}`);
    addStatRow(244, 392, 388, 'HP', `${Math.ceil(this.stats.hp)}/${Math.round(this.stats.maxHp)}`);
    addStatRow(244, 392, 410, 'БРОНЯ', `${Math.round(this.stats.armor * 100)}%`);
    addStatRow(244, 392, 432, 'ВАМПИРИЗМ', `${Math.round(this.stats.vampirism * 100)}%`);
    addStatRow(
      244,
      392,
      454,
      'РЕГЕН HP',
      `${this.stats.hpRegenPerSecond.toFixed(2).replace('.', ',')}/С`,
    );
    const settingsLabel = this.add
      .text(VIEW_WIDTH / 2, 553, 'НАСТРОЙКИ', {
        fontFamily: 'Georgia, serif',
        fontSize: '12px',
        color: '#777162',
        letterSpacing: 3,
      })
      .setOrigin(0.5);

    const makeButton = (
      y: number,
      label: string,
      primary = false,
    ): { box: Phaser.GameObjects.Rectangle; text: Phaser.GameObjects.Text } => {
      const baseColor = primary ? 0x51272c : 0x191d1a;
      const hoverColor = primary ? 0x6b3339 : 0x2a302b;
      const box = this.add
        .rectangle(VIEW_WIDTH / 2, y, 318, primary ? 62 : 54, baseColor, 1)
        .setStrokeStyle(2, primary ? COLORS.parchmentDark : 0x676253, primary ? 0.9 : 0.68)
        .setInteractive({ useHandCursor: true });
      const buttonText = this.add
        .text(VIEW_WIDTH / 2, y, label, {
          fontFamily: 'Georgia, serif',
          fontSize: primary ? '18px' : '15px',
          fontStyle: 'bold',
          color: primary ? '#efddb1' : '#c8bea2',
          letterSpacing: 1,
        })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });
      box.on('pointerover', () => box.setFillStyle(hoverColor, 1));
      box.on('pointerout', () => box.setFillStyle(baseColor, 1));
      overlay.add([box, buttonText]);
      return { box, text: buttonText };
    };

    overlay.add([
      veil,
      panel,
      innerPanel,
      sigil,
      title,
      hero,
      heroName,
      runState,
      statsPanel,
      statsTitle,
      statsDivider,
      ...statObjects,
      settingsLabel,
    ]);
    makeButton(510, 'ПРОДОЛЖИТЬ', true);
    const soundButton = makeButton(
      590,
      this.soundEnabled ? 'ЗВУК: ВКЛ' : 'ЗВУК: ВЫКЛ',
    );
    this.pauseSoundButtonText = soundButton.text;
    makeButton(655, 'ВЫЙТИ В ГЛАВНОЕ МЕНЮ');
    const pauseHint = this.add
      .text(VIEW_WIDTH / 2, 720, 'ESC / P — ПРОДОЛЖИТЬ', {
        fontSize: '10px',
        color: '#5f5b51',
        letterSpacing: 1,
      })
      .setOrigin(0.5)
      .setScrollFactor(0);
    overlay.add(pauseHint);

    this.pauseOverlay = overlay;
  }

  private closePause(): void {
    if (!this.manuallyPaused) return;
    this.pauseOverlay?.destroy(true);
    this.pauseOverlay = undefined;
    this.pauseSoundButtonText = undefined;
    this.manuallyPaused = false;
    if (!this.externallyPaused && !this.ended && !this.choosingUpgrade) this.physics.world.resume();
  }

  private endRun(victory: boolean): void {
    if (this.ended) return;
    this.ended = true;
    try {
      const nickname = loadNickname(window.localStorage);
      if (nickname) {
        recordRunResult(window.localStorage, nickname, {
          kills: this.kills,
          level: this.level,
          survivedSeconds: Math.floor(this.elapsedMs / 1000),
          victory,
        });
      }
    } catch {
      // Результат забега остаётся доступен даже при отключённом localStorage.
    }
    this.physics.world.pause();
    this.player.setVelocity(0, 0);
    this.releaseJoystick();

    const overlay = this.add.container(0, 0).setScrollFactor(0).setDepth(9000);
    overlay.add(
      this.add
        .rectangle(0, 0, VIEW_WIDTH, VIEW_HEIGHT, 0x050706, 0.93)
        .setOrigin(0),
    );
    overlay.add(
      this.add
        .text(VIEW_WIDTH / 2, 210, victory ? 'ВЫСТОЯЛ' : 'ПАЛ', {
          fontFamily: 'Georgia, serif',
          fontSize: '48px',
          color: victory ? '#d9c58c' : '#9f3c3c',
          stroke: '#16110d',
          strokeThickness: 7,
        })
        .setOrigin(0.5),
    );

    const livedSeconds = Math.floor(this.elapsedMs / 1000);
    const statsText = [
      `Время: ${Math.floor(livedSeconds / 60)}:${(livedSeconds % 60).toString().padStart(2, '0')}`,
      `Уровень: ${this.level}`,
      `Уничтожено: ${this.kills}`,
    ].join('\n');
    overlay.add(
      this.add
        .text(VIEW_WIDTH / 2, 310, statsText, {
          align: 'center',
          fontSize: '20px',
          lineSpacing: 12,
          color: '#c8bda4',
        })
        .setOrigin(0.5, 0),
    );

    const makeResultButton = (
      y: number,
      label: string,
      primary = false,
    ): void => {
      const width = 290;
      const height = primary ? 62 : 54;
      const baseColor = primary ? 0x4f2c2e : 0x141816;
      const hoverColor = primary ? 0x67363a : 0x222823;
      const button = this.add
        .container(VIEW_WIDTH / 2, y)
        .setSize(width, height)
        .setScrollFactor(0)
        .setInteractive(
          new Phaser.Geom.Rectangle(-width / 2, -height / 2, width, height),
          Phaser.Geom.Rectangle.Contains,
        );
      const background = this.add
        .rectangle(0, 0, width, height, baseColor, 1)
        .setStrokeStyle(primary ? 3 : 2, COLORS.parchmentDark, primary ? 0.9 : 0.62);
      const text = this.add
        .text(0, 0, label, {
          fontFamily: 'Georgia, serif',
          fontSize: primary ? '19px' : '16px',
          fontStyle: 'bold',
          color: primary ? '#eadbb3' : '#bdb294',
        })
        .setOrigin(0.5);
      button.add([background, text]);
      if (button.input) button.input.cursor = 'pointer';
      button.on('pointerover', () => background.setFillStyle(hoverColor, 1));
      button.on('pointerout', () => {
        background.setFillStyle(baseColor, 1);
        button.setScale(1);
      });
      overlay.add(button);
    };

    makeResultButton(495, 'НАЧАТЬ СНОВА', true);
    makeResultButton(575, 'В ГЛАВНОЕ МЕНЮ');
  }

  private performResultAction(action: ResultOverlayAction): void {
    if (this.resultTransitioning) return;
    this.resultTransitioning = true;
    if (action === 'restart') this.scene.restart();
    else this.scene.start('menu');
  }

  private bindLifecycle(): void {
    const pause = () => {
      this.externallyPaused = true;
      if (!this.ended) this.physics.world.pause();
    };
    const resume = () => {
      this.externallyPaused = false;
      if (!this.ended && !this.choosingUpgrade && !this.manuallyPaused) this.physics.world.resume();
    };
    const toggleManualPause = () => this.togglePause();

    this.game.events.on('kuogam:pause', pause);
    this.game.events.on('kuogam:resume', resume);
    this.input.keyboard?.on('keydown-ESC', toggleManualPause);
    this.input.keyboard?.on('keydown-P', toggleManualPause);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.game.events.off('kuogam:pause', pause);
      this.game.events.off('kuogam:resume', resume);
      this.input.keyboard?.off('keydown-ESC', toggleManualPause);
      this.input.keyboard?.off('keydown-P', toggleManualPause);
    });
  }
}
